import { createContext, useCallback, useContext, type ReactNode } from "react";
import { applyUrl as baseApplyUrl, type Lender } from "@/lib/lenders";

/**
 * Adtraction-spårning: lägger endast till epi/epi2 sist i query-strängen.
 * Befintliga parametrar (a, as, t, tk m.fl.) lämnas helt orörda.
 */
const ADTRACTION_HOSTS = [/^(go|do|track|cdn)\.adt\d+\.(com|net|se)$/i, /^do\.enklare\.se$/i, /^go\.creditstar\.se$/i, /^track\.adtraction\.com$/i];

export function isAdtractionUrl(url: string): boolean {
  try {
    const host = new URL(url).hostname;
    return ADTRACTION_HOSTS.some((re) => re.test(host));
  } catch {
    return false;
  }
}

export function withEpi(url: string, epi?: string, epi2?: string): string {
  if (!url || !isAdtractionUrl(url)) return url;
  let out = url;
  if (epi) {
    out = out.replace(/([?&])epi=[^&]*/i, "$1").replace(/[?&]$/, "");
    out += (out.includes("?") ? "&" : "?") + `epi=${encodeURIComponent(epi)}`;
  }
  if (epi2) {
    out = out.replace(/([?&])epi2=[^&]*/i, "$1").replace(/[?&]$/, "");
    out += (out.includes("?") ? "&" : "?") + `epi2=${encodeURIComponent(epi2)}`;
  }
  return out;
}

const EpiContext = createContext<string | undefined>(undefined);

export function EpiProvider({ value, children }: { value: string; children: ReactNode }) {
  return <EpiContext.Provider value={value}>{children}</EpiContext.Provider>;
}

export function useEpi(): string | undefined {
  return useContext(EpiContext);
}

/** Returnerar en funktion som bygger affiliate-URL med epi (sida) + epi2 (placering). */
export function useApplyUrl() {
  const epi = useContext(EpiContext);
  return useCallback((l: Lender, epi2?: string) => withEpi(baseApplyUrl(l), epi, epi2), [epi]);
}

/** Samma som ovan men för råa URL:er (t.ex. affiliateUrl från lenders.json). */
export function useTrackUrl() {
  const epi = useContext(EpiContext);
  return useCallback((url: string, epi2?: string) => withEpi(url, epi, epi2), [epi]);
}
